// Списък с фактури. Издател: своите фактури с изпращане/анулиране + CSV.
// Наемател: получените фактури (само преглед).

import React, { useCallback, useState } from 'react';
import { Linking, Text, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { api, API_URL, money } from '../api';
import { useAuth } from '../AuthContext';
import { showError, showSuccess } from '../toast';
import { confirmAction } from '../toast';
import { Badge, Button, Card, COLORS, Muted, Screen, Title } from '../ui';
import { getLocale, t } from '../i18n';

const KIND_LABELS: Record<string, string> = {
  INVOICE: 'Фактура',
  CREDIT_NOTE: 'Кредитно известие',
  DEBIT_NOTE: 'Дебитно известие',
};

export default function InvoicesScreen({ navigation }: any) {
  const { user } = useAuth();
  const isIssuer = user?.role === 'LANDLORD' || user?.role === 'AGENCY';
  const [invoices, setInvoices] = useState<any[]>([]);

  const load = useCallback(() => {
    api('/invoices').then((r) => setInvoices(r.invoices)).catch((e) => showError(e.message));
  }, []);
  useFocusEffect(load);

  const send = async (id: string) => {
    try {
      await api(`/invoices/${id}/send`, { body: {} });
      showSuccess(t('Фактурата е изпратена на наемателя.'));
      load();
    } catch (e: any) {
      showError(e.message);
    }
  };

  const cancel = (inv: any) => {
    confirmAction(t('Анулиране'), t('Анулиране на фактура № {number}? Не може да се редактира — само анулира.', { number: inv.number }), t('Анулирай'), async () => {
      try {
        await api(`/invoices/${inv.id}/cancel`, { body: { reason: 'Анулирана от издателя' } });
        showSuccess(t('Фактурата е анулирана.'));
        load();
      } catch (e: any) {
        showError(e.message);
      }
    });
  };

  return (
    <Screen>
      <Title>{t('Фактури')}</Title>
      {isIssuer && (
        <Button
          title={t('Изтегли CSV за счетоводител')}
          variant="secondary"
          onPress={() => Linking.openURL(`${API_URL}/invoices/export.csv`)}
        />
      )}
      {invoices.length === 0 && (
        <Card>
          <Muted>{isIssuer ? t('Още няма издадени фактури. Издайте от екрана на имота.') : t('Няма получени фактури.')}</Muted>
        </Card>
      )}
      {invoices.map((i) => (
        <Card key={i.id} style={i.status === 'CANCELLED' ? { opacity: 0.6 } : {}}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text style={{ fontWeight: '700', color: COLORS.text }}>
              {t('{kind} № {number}', { kind: t(KIND_LABELS[i.kind] ?? i.kind), number: i.number })}
            </Text>
            <Text style={{ fontWeight: '700', color: COLORS.primary }}>{money(i.totalCents)}</Text>
          </View>
          <Muted>
            {new Date(i.issueDate).toLocaleDateString(getLocale() === 'en' ? 'en-GB' : 'bg-BG')}
            {isIssuer && i.recipient ? ` · ${i.recipient}` : ''}
          </Muted>
          {i.status === 'CANCELLED' && <Badge text={t('АНУЛИРАНА')} color={COLORS.danger} />}

          <Button title={t('Отвори (PDF)')} variant="secondary" onPress={() => Linking.openURL(`${API_URL}${i.fileUrl}`)} />
          {isIssuer && i.status !== 'CANCELLED' && (
            <>
              {/* Статусът на изпращане — на собствен ред, не виси до бутон */}
              {i.sentToTenantAt && (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 10 }}>
                  <Text style={{ color: COLORS.success, fontWeight: '700' }}>✓</Text>
                  <Text style={{ color: COLORS.success, fontWeight: '600', fontSize: 13 }}>
                    {t('Изпратена на наемателя на {date}', { date: new Date(i.sentToTenantAt).toLocaleDateString(getLocale() === 'en' ? 'en-GB' : 'bg-BG') })}
                  </Text>
                </View>
              )}
              <View style={{ flexDirection: 'row', gap: 8 }}>
                {!i.sentToTenantAt && (
                  <View style={{ flex: 1 }}>
                    <Button title={t('Изпрати на наемателя')} onPress={() => send(i.id)} />
                  </View>
                )}
                <View style={{ flex: 1 }}>
                  <Button title={t('Анулирай')} variant="danger" onPress={() => cancel(i)} />
                </View>
              </View>
              {i.kind === 'INVOICE' && (
                <>
                  <Button
                    title={t('Редактирай (анулира и преиздава)')}
                    variant="secondary"
                    onPress={() =>
                      navigation.navigate('IssueInvoice', {
                        tenancyId: i.tenancyId,
                        reissueOf: {
                          id: i.id,
                          number: i.number,
                          paymentMethod: i.paymentMethod,
                          lines: (i.lines ?? []).map((l: any) => ({
                            name: l.name,
                            quantity: Number(l.quantity),
                            unitPriceCents: l.unitPriceCents,
                          })),
                        },
                      })
                    }
                  />
                  <Button
                    title={t('Коригирай (кредитно/дебитно известие)')}
                    variant="secondary"
                    onPress={() => navigation.navigate('CorrectInvoice', { invoiceId: i.id, number: i.number })}
                  />
                </>
              )}
            </>
          )}
        </Card>
      ))}
    </Screen>
  );
}
